// Funções puras de rotação/espelhamento de furos e rasgos do Editor MPR.
// Extraídas de EditorMPR.jsx para poderem ser testadas sem importar Canvas3D/three.js
// (three/examples/jsm usa ESM puro e quebra o transform padrão do Jest do CRA).
//
// Labels de furo horizontal (XP/XM/YP/YM) delegados a rotacionarLado
// (furoTransforms.js) — mesma convenção canônica CCW usada pelo backend
// (pose_engine.pose._PROJ / transforms.rotacionar_coord), em vez de tabelas
// locais próprias (ver docs/dividas/editores-reimplementam-transform-backend.md).

import { rotacionarLado, rotacionarLadoAprendido } from '../../utils/furoTransforms';

// Função pura — inverte a transformação para obter coordenadas no frame ORIGINAL
// Necessário ao adicionar furos enquanto uma rotação está ativa
export const inverterFuro = (furo, tipo, transf, pecaOrig) => {
  const { rotacao = 0, espelhado = false, invertido = false } = transf;
  const C = pecaOrig.comprimento;
  const L = pecaOrig.largura;
  const LADO_INV = { 'LS': 'LSU', 'LSL': 'LSU', 'LSU': 'LS' };
  let { x, y } = furo;
  let lado = furo.lado;

  // Desfaz espelhado primeiro (espelhado é aplicado APÓS rotação no forward)
  if (espelhado) {
    const novaLargura = (rotacao === 90 || rotacao === 270) ? C : L;
    y = novaLargura - y;
    if (tipo === 'horizontal') {
      if (lado === 'YM') lado = 'YP'; else if (lado === 'YP') lado = 'YM';
    }
  }

  // Desfaz rotação (inverso de computarTransformacaoPura). Desfazer uma rotação
  // forward de G° equivale a aplicar forward de (360-G)°: inverso de 90° é 270°,
  // inverso de 270° é 90°, 180° é seu próprio inverso.
  if (rotacao === 90) {
    // Forward 90°: nx = L-y, ny = x → Inverse: x_orig = y, y_orig = L - x
    const ox = y; const oy = L - x;
    x = ox; y = oy;
    if (tipo === 'horizontal') lado = rotacionarLado(lado, 270);
  } else if (rotacao === 180) {
    x = C - x; y = L - y;
    // NÃO migrado para rotacionarLadoAprendido (ciclo 7b): este furo é uma
    // adição nova, ainda não persistida em nenhuma lista — não existe corpus
    // de furos irmãos no MESMO frame (o da peça rotacionada) pra aprender a
    // convenção; pecaOrig.furosHorizontais está no frame ORIGINAL (o alvo
    // desta inversão, não a origem). Mantém a tabela fixa — mesmo
    // comportamento de antes deste ciclo.
    if (tipo === 'horizontal') lado = rotacionarLado(lado, 180);
  } else if (rotacao === 270) {
    // Forward 270°: nx = y, ny = C-x → Inverse: x_orig = C - y, y_orig = x
    const ox = C - y; const oy = x;
    x = ox; y = oy;
    if (tipo === 'horizontal') lado = rotacionarLado(lado, 90);
  }

  // Desfaz invertido (só afeta campo lado dos verticais)
  if (tipo === 'vertical' && invertido) lado = LADO_INV[lado] || lado;

  return { ...furo, x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10, lado };
};

// Função pura — inverte a transformação de um rasgo (x/y + direcao)
export const inverterRasgo = (rasgo, transf, pecaOrig) => {
  const { rotacao = 0, espelhado = false } = transf;
  const C = pecaOrig.comprimento;
  const L = pecaOrig.largura;
  let { x, y, direcao } = rasgo;

  if (espelhado) {
    const novaLargura = (rotacao === 90 || rotacao === 270) ? C : L;
    y = novaLargura - y;
  }

  if (rotacao === 90) {
    const ox = y; const oy = L - x;
    x = ox; y = oy;
    direcao = direcao === 'X' ? 'Y' : 'X';
  } else if (rotacao === 180) {
    x = C - x; y = L - y;
  } else if (rotacao === 270) {
    const ox = C - y; const oy = x;
    x = ox; y = oy;
    direcao = direcao === 'X' ? 'Y' : 'X';
  }

  return { ...rasgo, x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10, direcao };
};

// Função pura — sincroniza pecaOriginal após editar um campo do furo na TELA (peca).
// `index` é a posição do furo em peca.furos/furosHorizontais — a MESMA posição em
// pecaOriginal.furos/furosHorizontais (arrays alinhados por construção: load, adicionar,
// deletar e replicar furo sempre mantêm as duas listas em sincronia de índice/ordem).
// Não depende de uid — uid pode faltar em peças recém-importadas nunca salvas.
// Exceção conhecida: Agrupar/Desagrupar Furos altera peca sem atualizar pecaOriginal
// (lacuna pré-existente, fora deste conserto) — por isso o index pode ficar fora do
// alcance de pecaOriginal nesse caso; a guarda abaixo cobre isso com um aviso.
export const sincronizarFuroOriginal = (pecaOriginal, tipo, index, campo, novoValor, transformacao, furoTela) => {
  if (!pecaOriginal) return pecaOriginal;

  const chave = tipo === 'horizontal' ? 'furosHorizontais' : 'furos';
  const origFuros = [...(pecaOriginal[chave] || [])];

  if (index < 0 || index >= origFuros.length) {
    console.warn(
      `sincronizarFuroOriginal: index ${index} fora de pecaOriginal.${chave} (len=${origFuros.length}) — ` +
      `peca e pecaOriginal dessincronizadas (possível causa: Agrupar/Desagrupar Furos)`
    );
    return pecaOriginal;
  }

  if (campo === 'x' || campo === 'y') {
    const furoOrig = inverterFuro(furoTela, tipo, transformacao, pecaOriginal);
    origFuros[index] = {
      ...origFuros[index],
      x: furoOrig.x,
      y: furoOrig.y,
      ...(tipo === 'horizontal' ? { lado: furoOrig.lado } : {}),
    };
  } else {
    origFuros[index] = { ...origFuros[index], [campo]: novoValor };
  }

  return { ...pecaOriginal, [chave]: origFuros };
};

// Função pura — aplica transformação a partir do estado ORIGINAL da peça
// Não toca em nenhum estado React
export const computarTransformacaoPura = (pecaOrig, transf) => {
  const { rotacao = 0, espelhado = false, invertido = false } = transf;
  const LADO_INV = { 'LS': 'LSU', 'LSL': 'LSU', 'LSU': 'LS' };

  let novoComprimento = pecaOrig.comprimento;
  let novaLargura = pecaOrig.largura;
  if (rotacao === 90 || rotacao === 270) {
    novoComprimento = pecaOrig.largura;
    novaLargura = pecaOrig.comprimento;
  }

  const furos = (pecaOrig.furos || []).map(f => {
    let x = f.x, y = f.y;
    if (rotacao === 90)       { const nx = pecaOrig.largura - y;     const ny = x;                      x = nx; y = ny; }
    else if (rotacao === 180) { x = pecaOrig.comprimento - x;        y = pecaOrig.largura - y; }
    else if (rotacao === 270) { const nx = y;                        const ny = pecaOrig.comprimento - x; x = nx; y = ny; }
    if (espelhado) y = novaLargura - y;
    return { ...f, x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10,
      lado: invertido ? (LADO_INV[f.lado] || f.lado) : f.lado };
  });

  const furosHorizontais = (pecaOrig.furosHorizontais || []).map(f => {
    let x = f.x, y = f.y, lado = f.lado;
    if (rotacao === 90) {
      const nx = pecaOrig.largura - y; const ny = x; x = nx; y = ny;
      lado = rotacionarLado(lado, 90);
    } else if (rotacao === 180) {
      x = pecaOrig.comprimento - x; y = pecaOrig.largura - y;
      // Validado contra a regra oficial [CNC] (ciclo 9) — ver
      // docs/referencia/formato-mpr/campo-bm-horizontal.md §9 e furoTransforms.js.
      lado = rotacionarLadoAprendido(f, 180, pecaOrig.comprimento, pecaOrig.largura);
    } else if (rotacao === 270) {
      const nx = y; const ny = pecaOrig.comprimento - x; x = nx; y = ny;
      lado = rotacionarLado(lado, 270);
    }
    if (espelhado) {
      y = novaLargura - y;
      if (lado === 'YM') lado = 'YP'; else if (lado === 'YP') lado = 'YM';
    }
    return { ...f, x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10, lado };
  });

  const rasgos = (pecaOrig.rasgos || []).map(r => {
    let x = r.x, y = r.y, direcao = r.direcao;
    if (rotacao === 90)       { const nx = pecaOrig.largura - y;     const ny = x;                      x = nx; y = ny; direcao = direcao === 'X' ? 'Y' : 'X'; }
    else if (rotacao === 180) { x = pecaOrig.comprimento - x;        y = pecaOrig.largura - y; }
    else if (rotacao === 270) { const nx = y;                        const ny = pecaOrig.comprimento - x; x = nx; y = ny; direcao = direcao === 'X' ? 'Y' : 'X'; }
    if (espelhado) y = novaLargura - y;
    return { ...r, x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10, direcao };
  });

  return { comprimento: novoComprimento, largura: novaLargura, furos, furosHorizontais, rasgos };
};
